import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { notificationStore, userStore } from '../../store';
import { Bell, BellOff, Check, X } from 'lucide-react';

// NotificationDrawer — slide-down panel anchored to the top of the
// screen, showing the most recent D2P notifications.
//
// Renders nothing when closed. Renders an overlay + panel when open.
// Close handlers: tap backdrop, Esc, "X" button, or after marking all
// read (caller can decide).

const BRAND = '#5B47E0';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';
const SURFACE = '#F1F5F9';

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
}

const NotificationDrawer: React.FC<NotificationDrawerProps> = observer(({ open, onClose }) => {
  // Refresh on open so the badge count matches the list.
  useEffect(() => {
    if (open) notificationStore.fetch().catch(() => {});
  }, [open]);

  // Esc closes the drawer + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const isLoggedIn = userStore.isLoggedIn;
  const items = notificationStore.items;
  const hasUnread = notificationStore.unreadCount > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(15, 23, 42, 0.35)' }}
        aria-hidden="true"
      />

      {/* Panel — slides down from the top */}
      <div
        className="fixed left-0 right-0 top-0 z-50 bg-white rounded-b-3xl shadow-2xl"
        style={{
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        role="dialog"
        aria-label="Notifications"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 pt-5 pb-3 safe-area-top"
          style={{ borderBottom: '1px solid #F1F5F9' }}
        >
          <div>
            <h2
              className="text-[20px] font-bold tracking-tight"
              style={{ color: TEXT_PRIMARY }}
            >
              Notifications
            </h2>
            {hasUnread && (
              <p className="text-[12px] mt-0.5" style={{ color: TEXT_MUTED }}>
                {notificationStore.unreadCount} unread
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasUnread && isLoggedIn && (
              <button
                onClick={() => notificationStore.markAllAsRead()}
                disabled={notificationStore.mutating}
                className="text-[13px] font-medium px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
                style={{ color: BRAND, background: '#EDEAFC' }}
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Close"
              className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
              style={{ background: SURFACE }}
            >
              <X className="w-[18px] h-[18px]" style={{ color: TEXT_PRIMARY }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-3 py-2 safe-area-bottom">
          {!isLoggedIn ? (
            <EmptyState
              icon={<BellOff className="w-10 h-10" style={{ color: TEXT_MUTED }} />}
              title="Sign in for notifications"
              body="Log in to see updates about your orders, reviews, and reading milestones."
            />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Bell className="w-10 h-10" style={{ color: TEXT_MUTED }} />}
              title="No notifications"
              body="You're all caught up. We'll let you know when something new arrives."
            />
          ) : (
            <ul className="divide-y" style={{ borderColor: '#F1F5F9' }}>
              {items.map((n) => (
                <li key={n.id}>
                  <NotificationRow
                    notification={n}
                    onMarkRead={() => notificationStore.markAsRead([n.id])}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
});

function NotificationRow({
  notification,
  onMarkRead,
}: {
  notification: import('../../store/notificationStore').NotificationEntry;
  onMarkRead: () => void;
}) {
  const isUnread = !notification.read;
  return (
    <div
      className="px-3 py-3 flex gap-3 transition-colors active:bg-[#F8FAFC]"
      style={{ background: isUnread ? '#F8F5FE' : 'transparent' }}
    >
      {/* Unread dot indicator */}
      <div className="pt-1.5 w-2 flex-shrink-0">
        {isUnread && (
          <span
            className="block w-2 h-2 rounded-full"
            style={{ background: BRAND }}
            aria-label="Unread"
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p
          className="text-[14px] font-semibold leading-snug"
          style={{ color: TEXT_PRIMARY }}
        >
          {notification.title}
        </p>
        {notification.description && (
          <p
            className="text-[13px] mt-0.5 leading-snug"
            style={{ color: TEXT_MUTED }}
          >
            {notification.description}
          </p>
        )}
        <p
          className="text-[11px] mt-1"
          style={{ color: '#94A3B8' }}
        >
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>

      {isUnread && (
        <button
          onClick={onMarkRead}
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          style={{ background: SURFACE }}
          aria-label="Mark as read"
          title="Mark as read"
        >
          <Check className="w-[14px] h-[14px]" style={{ color: TEXT_MUTED }} />
        </button>
      )}
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center px-6">
      <div
        className="w-20 h-20 rounded-3xl flex items-center justify-center mb-4"
        style={{ background: SURFACE }}
      >
        {icon}
      </div>
      <p
        className="text-[16px] font-semibold"
        style={{ color: TEXT_PRIMARY }}
      >
        {title}
      </p>
      <p
        className="text-[13px] mt-1.5 max-w-xs"
        style={{ color: TEXT_MUTED }}
      >
        {body}
      </p>
    </div>
  );
}

// Minimal relative-time formatter. Avoids pulling in date-fns just for
// this one screen — KleverBook ships ~150 KB lighter without it.
function formatRelativeTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';
    const delta = Date.now() - then;
    const minute = 60_000;
    const hour = 60 * minute;
    const day = 24 * hour;
    if (delta < minute) return 'Just now';
    if (delta < hour) return `${Math.floor(delta / minute)}m ago`;
    if (delta < day) return `${Math.floor(delta / hour)}h ago`;
    if (delta < 7 * day) return `${Math.floor(delta / day)}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

export default NotificationDrawer;
